import React, { useState, useEffect } from 'react';
import { fromIso } from '../../utils/businessDate';
import { getTenantToday } from '../../utils/dateUtils';
import styled from 'styled-components';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendingUp,
  TrendingDown,
  RefreshCcw,
  Calendar,
  Download,
  Filter,
  BarChart3,
  PieChart,
  DollarSign,
  Percent,
  Users,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
  Info
} from 'lucide-react';
import { cn } from '../../lib/utils';

const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
  padding: 2rem;
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

interface PerformanceData {
  portfolioId: number;
  portfolioName: string;
  householdName: string;
  beginningValue: number;
  endingValue: number;
  netContributions: number;
  investmentChange: number;
  feesCharged: number;
  periodReturnTWR: number;
  periodReturnMWR: number;
  ytdReturn: number;
  oneYearReturn: number;
  threeYearReturn: number;
  fiveYearReturn: number;
  inceptionReturn: number;
  benchmarkReturn: number;
  alpha: number;
}

interface HouseholdPerformance {
  householdId: number;
  householdName: string;
  totalValue: number;
  ytdReturn: number;
  portfolios: PerformanceData[];
}

const PerformanceReportingScreen: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<'MTD' | 'QTD' | 'YTD' | '1Y' | '3Y' | 'ITD'>('YTD');
  const [households, setHouseholds] = useState<HouseholdPerformance[]>([]);
  const [selectedHousehold, setSelectedHousehold] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'summary' | 'detail' | 'attribution'>('summary');
  const [returnType, setReturnType] = useState<'TWR' | 'MWR'>('TWR');

  useEffect(() => {
    fetchPerformanceData();
  }, [selectedPeriod]);

  const fetchPerformanceData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/performance/households?period=${selectedPeriod}`);
      setHouseholds(response.data || []);
      if (response.data?.length > 0 && !selectedHousehold) {
        setSelectedHousehold(response.data[0].householdId);
      }
    } catch (error) {
      console.error('Failed to fetch performance data:', error);
      // No mock data - show empty state
      setHouseholds([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val);
  };

  const formatPercent = (val: number) => {
    const sign = val >= 0 ? '+' : '';
    return `${sign}${val.toFixed(2)}%`;
  };

  const selectedHouseholdData = households.find(h => h.householdId === selectedHousehold);

  const totalAUM = households.reduce((sum, h) => sum + h.totalValue, 0);
  const avgReturn = households.length > 0 ? households.reduce((sum, h) => sum + h.ytdReturn, 0) / households.length : 0;
  const benchmarkReturn = 7.82;

  return (
    <Container>
      <div className="max-w-[1800px] mx-auto space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-between items-end"
        >
          <div>
            <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-blue-500" />
              Performance Reporting
              <span className="ml-2 px-3 py-1 bg-blue-500/10 border border-blue-500/20 rounded-full text-xs font-bold text-blue-400 uppercase tracking-widest">
                RIA
              </span>
            </h1>
            <p className="text-slate-500 font-medium mt-1">
              Time-weighted and money-weighted returns with fee transparency
            </p>
          </div>
          <div className="flex gap-3">
            <div className="flex bg-slate-900 border border-white/10 p-1 rounded-xl">
              {['MTD', 'QTD', 'YTD', '1Y', '3Y', 'ITD'].map((period) => (
                <button
                  key={period}
                  onClick={() => setSelectedPeriod(period as any)}
                  className={cn(
                    "px-3 py-1.5 rounded-lg text-xs font-bold transition-all",
                    selectedPeriod === period ? "bg-blue-600 text-white shadow-lg" : "text-slate-500 hover:text-slate-300"
                  )}
                >
                  {period}
                </button>
              ))}
            </div>
            <button
              onClick={fetchPerformanceData}
              className="flex items-center gap-2 px-4 py-2 bg-slate-900 border border-white/10 rounded-xl text-sm font-bold text-slate-300 hover:text-white transition-all"
            >
              <RefreshCcw className={cn("w-4 h-4", loading && "animate-spin")} />
              Refresh
            </button>
          </div>
        </motion.div>

        {/* Summary Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          <GlassCard className="p-6 border-l-4 border-l-blue-500">
            <div className="flex justify-between items-start">
              <div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Total AUM</div>
                <div className="text-2xl font-bold text-white tracking-tight">{formatCurrency(totalAUM)}</div>
                <div className="text-[10px] text-slate-500 mt-2">{households.length} households</div>
              </div>
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center text-blue-400">
                <DollarSign className="w-5 h-5" />
              </div>
            </div>
          </GlassCard>

          <GlassCard className="p-6 border-l-4 border-l-emerald-500">
            <div className="flex justify-between items-start">
              <div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">{selectedPeriod} Return (TWR)</div>
                <div className={cn("text-2xl font-bold tracking-tight", avgReturn >= 0 ? "text-emerald-400" : "text-red-400")}>
                  {formatPercent(avgReturn)}
                </div>
                <div className="text-[10px] text-slate-500 mt-2 flex items-center gap-1">
                  <span>vs Benchmark:</span>
                  <span className={cn("font-bold", avgReturn > benchmarkReturn ? "text-emerald-400" : "text-red-400")}>
                    {formatPercent(avgReturn - benchmarkReturn)}
                  </span>
                </div>
              </div>
              <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center text-emerald-400">
                <TrendingUp className="w-5 h-5" />
              </div>
            </div>
          </GlassCard>

          <GlassCard className="p-6 border-l-4 border-l-violet-500">
            <div className="flex justify-between items-start">
              <div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Benchmark</div>
                <div className="text-2xl font-bold text-violet-400 tracking-tight">{formatPercent(benchmarkReturn)}</div>
                <div className="text-[10px] text-slate-500 mt-2">60/40 Portfolio</div>
              </div>
              <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center text-violet-400">
                <BarChart3 className="w-5 h-5" />
              </div>
            </div>
          </GlassCard>

          <GlassCard className="p-6 border-l-4 border-l-amber-500">
            <div className="flex justify-between items-start">
              <div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Fees Collected</div>
                <div className="text-2xl font-bold text-white tracking-tight">$24,750</div>
                <div className="text-[10px] text-slate-500 mt-2">0.87% effective rate</div>
              </div>
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center text-amber-400">
                <Percent className="w-5 h-5" />
              </div>
            </div>
          </GlassCard>
        </motion.div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Household List */}
          <GlassCard className="lg:col-span-1">
            <div className="p-4 border-b border-white/5 bg-slate-950/30">
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-400" />
                Households
              </h3>
            </div>
            <div className="divide-y divide-white/5">
              {households.map((household) => (
                <button
                  key={household.householdId}
                  onClick={() => setSelectedHousehold(household.householdId)}
                  className={cn(
                    "w-full p-4 text-left transition-all",
                    selectedHousehold === household.householdId
                      ? "bg-blue-500/10 border-l-2 border-l-blue-500"
                      : "hover:bg-white/5"
                  )}
                >
                  <div className="font-bold text-white text-sm">{household.householdName}</div>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-xs text-slate-500">{formatCurrency(household.totalValue)}</span>
                    <span className={cn(
                      "text-xs font-bold",
                      household.ytdReturn >= 0 ? "text-emerald-400" : "text-red-400"
                    )}>
                      {formatPercent(household.ytdReturn)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </GlassCard>

          {/* Performance Detail */}
          <div className="lg:col-span-3 space-y-6">
            {/* Return Type Toggle */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex bg-slate-900 border border-white/10 p-1 rounded-xl">
                  <button
                    onClick={() => setReturnType('TWR')}
                    className={cn(
                      "px-4 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-2",
                      returnType === 'TWR' ? "bg-blue-600 text-white shadow-lg" : "text-slate-500 hover:text-slate-300"
                    )}
                  >
                    TWR
                    <Info className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => setReturnType('MWR')}
                    className={cn(
                      "px-4 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-2",
                      returnType === 'MWR' ? "bg-blue-600 text-white shadow-lg" : "text-slate-500 hover:text-slate-300"
                    )}
                  >
                    MWR
                    <Info className="w-3 h-3" />
                  </button>
                </div>
                <span className="text-xs text-slate-500">
                  {returnType === 'TWR' ? 'Time-Weighted Return (portfolio performance)' : 'Money-Weighted Return (personal rate of return)'}
                </span>
              </div>
              <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-xl transition-all">
                <Download className="w-4 h-4" />
                Generate Statement
              </button>
            </div>

            {/* Account Performance Table */}
            <GlassCard>
              <div className="p-4 border-b border-white/5 bg-slate-950/30 flex items-center justify-between">
                <h3 className="text-lg font-bold text-white">
                  {selectedHouseholdData?.householdName || 'Select Household'} - Account Performance
                </h3>
                <span className="text-xs text-slate-500">As of {fromIso(getTenantToday()).toLocaleDateString()}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5 bg-slate-950/20">
                      <th className="py-4 px-4">Account</th>
                      <th className="py-4 px-4 text-right">Beginning</th>
                      <th className="py-4 px-4 text-right">+/- Flows</th>
                      <th className="py-4 px-4 text-right">Investment Chg</th>
                      <th className="py-4 px-4 text-right">Fees</th>
                      <th className="py-4 px-4 text-right">Ending</th>
                      <th className="py-4 px-4 text-right">{returnType}</th>
                      <th className="py-4 px-4 text-right">vs Benchmark</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {selectedHouseholdData?.portfolios.map((portfolio) => {
                      const returnVal = returnType === 'TWR' ? portfolio.periodReturnTWR : portfolio.periodReturnMWR;
                      const alpha = returnVal - portfolio.benchmarkReturn;
                      return (
                        <tr key={portfolio.portfolioId} className="group hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                          <td className="py-4 px-4">
                            <div className="font-bold text-white">{portfolio.portfolioName}</div>
                          </td>
                          <td className="py-4 px-4 text-right font-mono text-slate-400">
                            {formatCurrency(portfolio.beginningValue)}
                          </td>
                          <td className="py-4 px-4 text-right font-mono text-blue-400">
                            {portfolio.netContributions >= 0 ? '+' : ''}{formatCurrency(portfolio.netContributions)}
                          </td>
                          <td className={cn("py-4 px-4 text-right font-mono font-bold", portfolio.investmentChange >= 0 ? "text-emerald-400" : "text-red-400")}>
                            {portfolio.investmentChange >= 0 ? '+' : ''}{formatCurrency(portfolio.investmentChange)}
                          </td>
                          <td className="py-4 px-4 text-right font-mono text-amber-400">
                            -{formatCurrency(portfolio.feesCharged)}
                          </td>
                          <td className="py-4 px-4 text-right font-mono font-bold text-white">
                            {formatCurrency(portfolio.endingValue)}
                          </td>
                          <td className={cn("py-4 px-4 text-right font-bold", returnVal >= 0 ? "text-emerald-400" : "text-red-400")}>
                            <div className="flex items-center justify-end gap-1">
                              {returnVal >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                              {formatPercent(returnVal)}
                            </div>
                          </td>
                          <td className={cn("py-4 px-4 text-right font-bold", alpha >= 0 ? "text-emerald-400" : "text-red-400")}>
                            {formatPercent(alpha)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {selectedHouseholdData && (
                    <tfoot className="bg-slate-900/50">
                      <tr className="text-sm font-bold border-t border-white/10">
                        <td className="py-4 px-4 text-white">Household Total</td>
                        <td className="py-4 px-4 text-right font-mono text-white">
                          {formatCurrency(selectedHouseholdData.portfolios.reduce((s, p) => s + p.beginningValue, 0))}
                        </td>
                        <td className="py-4 px-4 text-right font-mono text-blue-400">
                          +{formatCurrency(selectedHouseholdData.portfolios.reduce((s, p) => s + p.netContributions, 0))}
                        </td>
                        <td className="py-4 px-4 text-right font-mono text-emerald-400">
                          +{formatCurrency(selectedHouseholdData.portfolios.reduce((s, p) => s + p.investmentChange, 0))}
                        </td>
                        <td className="py-4 px-4 text-right font-mono text-amber-400">
                          -{formatCurrency(selectedHouseholdData.portfolios.reduce((s, p) => s + p.feesCharged, 0))}
                        </td>
                        <td className="py-4 px-4 text-right font-mono text-white">
                          {formatCurrency(selectedHouseholdData.totalValue)}
                        </td>
                        <td className={cn("py-4 px-4 text-right", selectedHouseholdData.ytdReturn >= 0 ? "text-emerald-400" : "text-red-400")}>
                          {formatPercent(selectedHouseholdData.ytdReturn)}
                        </td>
                        <td className="py-4 px-4 text-right text-emerald-400">
                          {formatPercent(selectedHouseholdData.ytdReturn - benchmarkReturn)}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </GlassCard>

            {/* Historical Returns */}
            <GlassCard>
              <div className="p-4 border-b border-white/5 bg-slate-950/30">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-violet-400" />
                  Historical Performance
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5 bg-slate-950/20">
                      <th className="py-4 px-4">Account</th>
                      <th className="py-4 px-4 text-right">YTD</th>
                      <th className="py-4 px-4 text-right">1 Year</th>
                      <th className="py-4 px-4 text-right">3 Year</th>
                      <th className="py-4 px-4 text-right">5 Year</th>
                      <th className="py-4 px-4 text-right">Since Inception</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {selectedHouseholdData?.portfolios.map((portfolio) => (
                      <tr key={portfolio.portfolioId} className="group hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                        <td className="py-4 px-4 font-bold text-white">{portfolio.portfolioName}</td>
                        <td className={cn("py-4 px-4 text-right font-bold", portfolio.ytdReturn >= 0 ? "text-emerald-400" : "text-red-400")}>
                          {formatPercent(portfolio.ytdReturn)}
                        </td>
                        <td className={cn("py-4 px-4 text-right font-bold", portfolio.oneYearReturn >= 0 ? "text-emerald-400" : "text-red-400")}>
                          {formatPercent(portfolio.oneYearReturn)}
                        </td>
                        <td className={cn("py-4 px-4 text-right font-bold", portfolio.threeYearReturn >= 0 ? "text-emerald-400" : "text-red-400")}>
                          {formatPercent(portfolio.threeYearReturn)}
                        </td>
                        <td className={cn("py-4 px-4 text-right font-bold", portfolio.fiveYearReturn >= 0 ? "text-emerald-400" : "text-red-400")}>
                          {formatPercent(portfolio.fiveYearReturn)}
                        </td>
                        <td className={cn("py-4 px-4 text-right font-bold", portfolio.inceptionReturn >= 0 ? "text-emerald-400" : "text-red-400")}>
                          {formatPercent(portfolio.inceptionReturn)}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-slate-900/30 border-t border-white/10">
                      <td className="py-4 px-4 font-bold text-slate-400">Benchmark (60/40)</td>
                      <td className="py-4 px-4 text-right text-slate-400">{formatPercent(7.82)}</td>
                      <td className="py-4 px-4 text-right text-slate-400">{formatPercent(10.45)}</td>
                      <td className="py-4 px-4 text-right text-slate-400">{formatPercent(8.12)}</td>
                      <td className="py-4 px-4 text-right text-slate-400">{formatPercent(8.95)}</td>
                      <td className="py-4 px-4 text-right text-slate-400">{formatPercent(9.25)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </GlassCard>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default PerformanceReportingScreen;
